import { VonqMapping } from "../types/vonqMapping";
import { employmentTypeOptions } from "../constant/VonqConstant";

export const REMOTE_WORK_OPTIONS = [
  { label: "Yes", value: "yes" as const },
  { label: "No", value: "no" as const },
] as const;

export type RemoteWorkChoice = (typeof REMOTE_WORK_OPTIONS)[number]["value"] | "";

/** @deprecated use REMOTE_WORK_OPTIONS — kept for legacy job spec normalization */
export const WORK_LOCATION_RATIO_OPTIONS = ["On-site", "Hybrid", "Remote"] as const;

export type WorkLocationRatioOption = (typeof WORK_LOCATION_RATIO_OPTIONS)[number];

export function resolveEmploymentType(
  contractType: string,
  existingEmploymentType?: string
): string | undefined {
  if (existingEmploymentType) return existingEmploymentType;

  const trimmed = String(contractType || "").trim();
  if (!trimmed) return undefined;

  const byLabelOrValue = employmentTypeOptions.find(
    (option) =>
      option.label === trimmed ||
      option.value === trimmed ||
      option.value === trimmed.toLowerCase()
  );
  if (byLabelOrValue) return byLabelOrValue.value;

  return mapLegacyContractTypeToEmploymentType(trimmed);
}

export function mapLegacyContractTypeToEmploymentType(
  contractType: string
): string | undefined {
  const value = String(contractType || "").trim().toLowerCase();
  if (!value) return undefined;

  if (value === "temporary") return "temporary";
  if (value === "fixed" || value.includes("fixed term")) return "fixed_term";
  if (value === "freelance") return "freelance";
  if (value === "permanent") return "permanent";
  if (value === "internship") return "internship";
  if (value === "traineeship") return "traineeship";
  if (value === "other" || value === "others") return "others";

  return undefined;
}

/** @deprecated use resolveEmploymentType */
export function mapContractTypeToEmploymentType(
  contractType: string
): string | undefined {
  return resolveEmploymentType(contractType);
}

export function mapRemoteChoiceToAllowsRemoteWork(
  choice: string
): 0 | 1 | undefined {
  const value = String(choice || "").trim().toLowerCase();
  if (value === "yes") return 1;
  if (value === "no") return 0;
  return undefined;
}

export function mapAllowsRemoteWorkToWorkLocationRatio(
  allowsRemoteWork?: 0 | 1 | null
): string {
  if (allowsRemoteWork === 1) return "Remote";
  if (allowsRemoteWork === 0) return "On-site";
  return "";
}

export function deriveRemoteWorkChoice(
  workLocationRatio: string,
  allowsRemoteWork?: 0 | 1 | null
): RemoteWorkChoice | "" {
  if (allowsRemoteWork === 1) return "yes";
  if (allowsRemoteWork === 0) return "no";

  const normalized = normalizeWorkLocationRatio(workLocationRatio);
  if (normalized === "Remote" || normalized === "Hybrid") return "yes";
  if (normalized === "On-site") return "no";
  return "";
}

export function normalizeContractTypeLabel(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed === "Please select position") return "";

  const legacyMap: Record<string, string> = {
    Fixed: "Fixed Term",
    Other: "Others",
  };
  if (legacyMap[trimmed]) return legacyMap[trimmed];

  const match = employmentTypeOptions.find(
    (option) => option.label === trimmed || option.value === trimmed
  );
  return match?.label || trimmed;
}

export function mapWorkLocationRatioToRemote(
  workLocationRatio: string
): 0 | 1 | undefined {
  const value = String(workLocationRatio || "").toLowerCase();
  if (!value) return undefined;
  if (value === "hybrid" || value.includes("hybrid")) return 1;
  if (value === "remote" || value.includes("remote")) return 1;
  if (
    value === "on-site" ||
    value.includes("on-site") ||
    value.includes("onsite") ||
    value.includes("on site")
  ) {
    return 0;
  }
  return undefined;
}

export function normalizeWorkLocationRatio(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const exact = WORK_LOCATION_RATIO_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase()
  );
  if (exact) return exact;

  const remoteFlag = mapWorkLocationRatioToRemote(trimmed);
  if (remoteFlag === 0) return "On-site";
  if (remoteFlag === 1) {
    return trimmed.toLowerCase().includes("hybrid") ? "Hybrid" : "Remote";
  }

  return "";
}

export function mapSeniorityToExperienceLevel(seniorityName: string): string {
  const value = String(seniorityName || "").toLowerCase();
  if (!value) return "";

  if (
    value.includes("no experience") ||
    value.includes("entry") ||
    value.includes("intern") ||
    value.includes("graduate")
  ) {
    return "No experience required";
  }
  if (
    value.includes("junior") ||
    value.includes("associate") ||
    value.includes("starter")
  ) {
    return "0 - 3 years of experience";
  }
  if (value.includes("mid") || value.includes("medior")) {
    return "3 - 5 years of experience";
  }
  if (value.includes("senior") && !value.includes("executive")) {
    return "5 - 10 years of experience";
  }
  if (
    value.includes("executive") ||
    value.includes("director") ||
    value.includes("lead") ||
    value.includes("manager")
  ) {
    return "More than 10 years of experience";
  }

  return seniorityName;
}

export interface StructuredWeeklyHours {
  from: string;
  to: string;
}

export interface StructuredSalary {
  from: string;
  to: string;
  period: string;
  currency: string;
}

export interface JobSpecCompensationFields {
  hoursPerWeek?: string;
  salaryRange?: string;
  defaultCurrency?: string;
  remoteWorkChoice?: string;
  weeklyHoursFrom?: string;
  weeklyHoursTo?: string;
  salaryFrom?: string;
  salaryTo?: string;
  salaryPeriod?: string;
  salaryCurrency?: string;
}

function parseNumericField(value?: string): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function parseHoursPerWeek(
  hoursText?: string
): { from: number; to: number } | undefined {
  const raw = String(hoursText || "").trim();
  if (!raw) return undefined;

  const numbers = raw.match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) return undefined;

  if (numbers.length === 1) {
    const value = Number(numbers[0]);
    if (!Number.isFinite(value)) return undefined;
    return { from: value, to: value };
  }

  const a = Number(numbers[0]);
  const b = Number(numbers[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return undefined;
  return { from: Math.min(a, b), to: Math.max(a, b) };
}

export function hydrateWeeklyHoursFromSources(
  hoursPerWeek?: string,
  mapping?: VonqMapping,
  explicit?: Pick<JobSpecCompensationFields, "weeklyHoursFrom" | "weeklyHoursTo">
): StructuredWeeklyHours {
  if (explicit?.weeklyHoursFrom?.trim() || explicit?.weeklyHoursTo?.trim()) {
    const from = String(explicit.weeklyHoursFrom || "").trim();
    const to = String(explicit.weeklyHoursTo || explicit.weeklyHoursFrom || "").trim();
    return { from, to };
  }

  const structured = mapping?.weeklyWorkingHours;
  if (structured?.from != null || structured?.to != null) {
    return {
      from: structured.from != null ? String(structured.from) : "",
      to:
        structured.to != null
          ? String(structured.to)
          : structured.from != null
            ? String(structured.from)
            : "",
    };
  }

  const parsed = parseHoursPerWeek(hoursPerWeek);
  if (parsed) {
    return { from: String(parsed.from), to: String(parsed.to) };
  }

  return { from: "", to: "" };
}

export function hydrateSalaryFromSources(
  salaryRange?: string,
  mapping?: VonqMapping,
  defaultCurrency?: string,
  explicit?: Pick<
    JobSpecCompensationFields,
    "salaryFrom" | "salaryTo" | "salaryPeriod" | "salaryCurrency"
  >
): StructuredSalary {
  if (
    explicit?.salaryFrom?.trim() ||
    explicit?.salaryTo?.trim() ||
    explicit?.salaryPeriod?.trim() ||
    explicit?.salaryCurrency?.trim()
  ) {
    return {
      from: String(explicit.salaryFrom || "").trim(),
      to: String(explicit.salaryTo || "").trim(),
      period: explicit.salaryPeriod?.trim() || "yearly",
      currency: explicit.salaryCurrency?.trim() || defaultCurrency || "",
    };
  }

  const salary = mapping?.salaryIndication as
    | {
        period?: string;
        range?: { from?: number; to?: number; currency?: string | null };
      }
    | undefined;

  if (salary?.range) {
    const toValue = salary.range.to;
    return {
      from:
        salary.range.from != null && salary.range.from !== 0
          ? String(salary.range.from)
          : "",
      to: toValue != null && toValue !== 0 ? String(toValue) : "",
      period: salary.period || "yearly",
      currency: salary.range.currency || defaultCurrency || "",
    };
  }

  const parsed = parseSalaryRange(salaryRange, defaultCurrency);
  if (parsed) {
    const range = parsed.range as {
      from?: number;
      to?: number;
      currency?: string | null;
    };
    return {
      from:
        range.from != null && range.from !== 0 ? String(range.from) : "",
      to: range.to != null && range.to !== 0 ? String(range.to) : "",
      period: String(parsed.period || "yearly"),
      currency: range.currency || defaultCurrency || "",
    };
  }

  return {
    from: "",
    to: "",
    period: "yearly",
    currency: defaultCurrency || "",
  };
}

export function formatHoursPerWeekText(from: string, to: string): string {
  const fromText = String(from || "").trim();
  const toText = String(to || "").trim();
  if (!fromText && !toText) return "";
  if (fromText && toText && fromText !== toText) {
    return `${fromText}-${toText}`;
  }
  return fromText || toText;
}

export function formatSalaryRangeText(
  from: string,
  to: string,
  period: string,
  currency: string
): string {
  const fromText = String(from || "").trim();
  const toText = String(to || "").trim();
  if (!fromText && !toText) return "";

  const amount =
    fromText && toText && fromText !== toText
      ? `${fromText}-${toText}`
      : toText || fromText;
  const parts = [amount, currency, period].filter(Boolean);
  return parts.join(" ");
}

export function buildWeeklyWorkingHoursFromStructured(
  from?: string,
  to?: string,
  hoursPerWeek?: string
): { from: number; to: number } | undefined {
  const fromNum = parseNumericField(from);
  const toNum = parseNumericField(to);

  if (fromNum != null || toNum != null) {
    const resolvedFrom = fromNum ?? toNum ?? 0;
    const resolvedTo = toNum ?? fromNum ?? resolvedFrom;
    return {
      from: resolvedFrom,
      to: Math.max(resolvedFrom, resolvedTo),
    };
  }

  return parseHoursPerWeek(hoursPerWeek);
}

/** Normalize to ISO 4217 code; never returns null. */
export function normalizeSalaryCurrency(
  currency?: string | null,
  fallback?: string | null
): string | undefined {
  const candidate = String(currency || fallback || "")
    .trim()
    .toUpperCase();
  if (candidate.length === 3 && /^[A-Z]{3}$/.test(candidate)) {
    return candidate;
  }
  return undefined;
}

function buildSalaryIndicationRange(
  from: number,
  to: number,
  period: string,
  currency?: string,
  defaultCurrency?: string
): Record<string, unknown> {
  const resolvedPeriod = period?.trim() || "yearly";
  const range: Record<string, unknown> = {
    from,
    to: Math.max(from, to),
  };
  const resolvedCurrency = normalizeSalaryCurrency(currency, defaultCurrency);
  if (resolvedCurrency) {
    range.currency = resolvedCurrency;
  }
  return {
    period: resolvedPeriod,
    range,
  };
}

export function buildSalaryIndicationFromStructured(
  from?: string,
  to?: string,
  period?: string,
  currency?: string,
  salaryRange?: string,
  defaultCurrency?: string
): Record<string, unknown> | undefined {
  const fromNum = parseNumericField(from);
  const toNum = parseNumericField(to);
  const resolvedPeriod = period?.trim() || "yearly";

  if (fromNum != null || toNum != null) {
    const resolvedFrom = fromNum ?? 0;
    const resolvedTo = toNum ?? resolvedFrom;
    return buildSalaryIndicationRange(
      resolvedFrom,
      resolvedTo,
      resolvedPeriod,
      currency,
      defaultCurrency
    );
  }

  return parseSalaryRange(salaryRange, defaultCurrency || undefined);
}

export function parseSalaryRange(
  salaryText?: string,
  defaultCurrency?: string
): Record<string, unknown> | undefined {
  const raw = String(salaryText || "").trim();
  if (!raw) return undefined;

  const numbers = raw.match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) return undefined;

  let period = "yearly";
  const lower = raw.toLowerCase();
  if (lower.includes("hour")) period = "hourly";
  else if (lower.includes("month")) period = "monthly";

  if (numbers.length === 1) {
    const to = Number(numbers[0]);
    if (!Number.isFinite(to)) return undefined;
    return buildSalaryIndicationRange(0, to, period, undefined, defaultCurrency);
  }

  const firstValue = Number(numbers[0]);
  const secondValue = Number(numbers[1]);
  if (!Number.isFinite(firstValue) || !Number.isFinite(secondValue)) {
    return undefined;
  }

  return buildSalaryIndicationRange(
    Math.min(firstValue, secondValue),
    Math.max(firstValue, secondValue),
    period,
    undefined,
    defaultCurrency
  );
}

export function buildJobSpecVonqMapping(
  mapping: VonqMapping,
  contractType: string,
  workLocationRatio: string,
  compensation: JobSpecCompensationFields = {}
): VonqMapping {
  const employmentType = resolveEmploymentType(
    contractType,
    mapping.employmentType
  );
  const allowsRemoteWork =
    mapping.allowsRemoteWork !== undefined && mapping.allowsRemoteWork !== null
      ? mapping.allowsRemoteWork
      : mapRemoteChoiceToAllowsRemoteWork(compensation.remoteWorkChoice || "") ??
        mapWorkLocationRatioToRemote(workLocationRatio);
  const weeklyWorkingHours = buildWeeklyWorkingHoursFromStructured(
    compensation.weeklyHoursFrom,
    compensation.weeklyHoursTo,
    compensation.hoursPerWeek
  );
  const salaryIndication = buildSalaryIndicationFromStructured(
    compensation.salaryFrom,
    compensation.salaryTo,
    compensation.salaryPeriod,
    compensation.salaryCurrency,
    compensation.salaryRange,
    compensation.defaultCurrency
  );

  const { salaryIndication: _staleSalary, ...mappingWithoutSalary } = mapping;

  return {
    ...mappingWithoutSalary,
    ...(employmentType ? { employmentType } : {}),
    ...(allowsRemoteWork !== undefined ? { allowsRemoteWork } : {}),
    ...(weeklyWorkingHours ? { weeklyWorkingHours } : {}),
    ...(salaryIndication ? { salaryIndication } : {}),
    syncSource: "job_spec_form",
    syncedAt: new Date().toISOString(),
  };
}

/** Rebuild vonqMapping from accumulated job spec form data before create/update. */
export function finalizeJobSpecVonqMapping(
  formData?: Record<string, unknown> | null
): VonqMapping {
  const data = formData || {};
  const vonqMapping = (data.vonqMapping as VonqMapping) || {};
  const remoteWorkChoice = deriveRemoteWorkChoice(
    String(data.workLocationRatio || ""),
    vonqMapping.allowsRemoteWork
  );

  return buildJobSpecVonqMapping(
    vonqMapping,
    String(data.contractType || ""),
    String(data.workLocationRatio || ""),
    {
      hoursPerWeek: data.hoursPerWeek as string | undefined,
      salaryRange: data.salaryRange as string | undefined,
      defaultCurrency: data.defaultCurrency as string | undefined,
      remoteWorkChoice: remoteWorkChoice || undefined,
      weeklyHoursFrom: data.weeklyHoursFrom as string | undefined,
      weeklyHoursTo: data.weeklyHoursTo as string | undefined,
      salaryFrom: data.salaryFrom as string | undefined,
      salaryTo: data.salaryTo as string | undefined,
      salaryPeriod: data.salaryPeriod as string | undefined,
      salaryCurrency: data.salaryCurrency as string | undefined,
    }
  );
}
